import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import RichTextEditor from './common/RichTextEditor';
import './BannerManager.css';

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    type: 'home',
    link: '',
    isActive: true,
    order: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      console.log('[BannerManager] Fetching banners from:', `${API_URL}/api/banners`);
      console.log('[BannerManager] Token exists:', !!token);

      const response = await axios.get(`${API_URL}/api/banners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('[BannerManager] Banners response:', response.data);
      
      if (response.data && response.data.success && response.data.data) {
        setBanners(response.data.data);
      } else {
        console.error('[BannerManager] Invalid response structure:', response.data);
        toast.error('Invalid response from server');
        setBanners([]);
      }
    } catch (error) {
      console.error('[BannerManager] Banners fetch error:', error);
      console.error('[BannerManager] Error response:', error.response?.data);
      console.error('[BannerManager] Error status:', error.response?.status);
      console.error('[BannerManager] Error message:', error.message);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch banners';
      
      toast.error(errorMessage);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData({ ...formData, image: response.data.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Validate title - strip HTML tags and check if there's actual text
    const titleText = formData.title.replace(/<[^>]*>/g, '').trim();
    if (!titleText) {
      toast.error('Please enter a banner title');
      return false;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      if (editingBanner) {
        await axios.put(
          `${API_URL}/api/banners/${editingBanner._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Banner updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/banners`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Banner created successfully!');
      }
      fetchBanners();
      resetForm();
    } catch (error) {
      console.error('Banner submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
      // Don't reset form on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      description: banner.description || '',
      image: banner.image,
      type: banner.type,
      link: banner.link || '',
      isActive: banner.isActive,
      order: banner.order || 0,
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Banner',
      message: 'Are you sure you want to delete this banner? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/banners/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success('Banner deleted successfully!');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
          fetchBanners();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete banner');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      type: 'home',
      link: '',
      isActive: true,
      order: 0,
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  return (
    <div className="banner-manager">
      <div className="header">
        <h2>Banner Manager</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Add Banner'}
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h3>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }} 
            noValidate
          >
            <div className="form-group">
              <RichTextEditor
                label="Title *"
                value={formData.title}
                onChange={(html) => setFormData({ ...formData, title: html })}
                placeholder="Timeless Artistry for Modern Living"
              />
              <div style={{ marginTop: '10px', padding: '12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <small style={{ display: 'block', marginBottom: '6px', color: '#374151', fontSize: '12px', fontWeight: '600' }}>
                  ✨ Luxe Design Recommendations:
                </small>
                <ul style={{ margin: '0', paddingLeft: '18px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <li>Use <strong>large font sizes</strong> (Size: 48px-72px recommended) for elegant impact</li>
                  <li>Keep text <strong>light weight</strong> (avoid bold/heavy fonts) for refined luxury look</li>
                  <li>Use <strong>serif fonts</strong> (like Cormorant Garamond style) for classy appearance</li>
                  <li>Recommended colors: <strong>White (#FFFFFF)</strong> for dark backgrounds or <strong>Dark Gray (#111827)</strong> for light backgrounds</li>
                  <li>Keep title <strong>concise</strong> (3-8 words) for maximum elegance</li>
                </ul>
                <small style={{ display: 'block', marginTop: '8px', color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
                  💡 Tip: Select text first, then apply formatting (color, size, bold, italic, etc.)
                </small>
              </div>
            </div>

            <div className="form-group">
              <RichTextEditor
                label="Description"
                value={formData.description}
                onChange={(html) => setFormData({ ...formData, description: html })}
                placeholder="Discover exceptional handcrafted pieces from master artisans around the world. Each creation tells a story of tradition, passion, and unparalleled craftsmanship."
              />
              <div style={{ marginTop: '10px', padding: '12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <small style={{ display: 'block', marginBottom: '6px', color: '#374151', fontSize: '12px', fontWeight: '600' }}>
                  ✨ Luxe Design Recommendations:
                </small>
                <ul style={{ margin: '0', paddingLeft: '18px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  <li>Use <strong>medium font sizes</strong> (Size: 18px-24px recommended) for readability</li>
                  <li>Keep text <strong>light to medium weight</strong> for elegant appearance</li>
                  <li>Use <strong>neutral colors</strong> like White (#FFFFFF) or Light Gray (#F3F4F6) for dark backgrounds</li>
                  <li>Keep description to <strong>2-3 sentences</strong> (40-80 words) for optimal impact</li>
                  <li>Maintain <strong>generous line spacing</strong> (leading-relaxed) for luxury feel</li>
                </ul>
                <small style={{ display: 'block', marginTop: '8px', color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
                  💡 Tip: Use the toolbar above to format your description with colors, sizes, bold, italic, and alignment
                </small>
              </div>
            </div>

            <div className="form-group">
              <label>Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="home">Home Banner</option>
                <option value="banner">General Banner</option>
                <option value="hero">Hero Banner</option>
              </select>
            </div>

            <div className="form-group">
              <label>Image *</label>
              <div style={{ marginBottom: '10px', padding: '12px', background: '#f0f7ff', borderRadius: '6px', border: '1px solid #b3d9ff' }}>
                <strong style={{ display: 'block', marginBottom: '8px', color: '#0066cc', fontSize: '13px' }}>📐 Ideal Image Size:</strong>
                {formData.type === 'hero' ? (
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    <strong>Hero Banner:</strong> 1920 x 1080 pixels (16:9 ratio) or 1920 x 600 pixels (16:5 ratio) - Full-width hero banner on homepage<br/>
                    <span style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>Recommended: High-quality, elegant imagery that matches luxury aesthetic</span>
                  </span>
                ) : formData.type === 'home' ? (
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    <strong>Home Banner:</strong> 1920 x 1080 pixels (16:9 ratio) or 1920 x 600 pixels (16:5 ratio) - Full-width banner on homepage<br/>
                    <span style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>Recommended: High-quality, elegant imagery that matches luxury aesthetic</span>
                  </span>
                ) : (
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    <strong>General Banner:</strong> 1920 x 600 pixels (16:5 ratio) - Recommended for all banner types<br/>
                    <span style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>Recommended: High-quality, elegant imagery that matches luxury aesthetic</span>
                  </span>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && <p>Uploading...</p>}
              {formData.image && (
                <div className="image-preview">
                  <img src={formData.image} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Link (Optional)</label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label>Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingBanner ? 'Update' : 'Create'} Banner
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading banners...</p>
      ) : (
        <div className="banners-grid">
          {banners.map((banner) => (
            <div key={banner._id} className="banner-card">
              <img src={banner.image} alt={banner.title.replace(/<[^>]*>/g, '') || 'Banner'} />
              <div className="banner-info">
                <h4 dangerouslySetInnerHTML={{ __html: banner.title }} />
                <p className="banner-type">Type: {banner.type}</p>
                <p className="banner-status">
                  Status: {banner.isActive ? 'Active' : 'Inactive'}
                </p>
                <div className="banner-actions">
                  <button onClick={() => handleEdit(banner)} className="btn-edit">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(banner._id)} className="btn-delete">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default BannerManager;

